import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import QRCode from "qrcode";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Download, Copy, Check, Upload, QrCode, ExternalLink, Search,
  LogOut, Loader2, Link2, Pencil, FileAudio, BarChart3,
} from "lucide-react";

const ORIGEM_META = [
  { key: "meta", label: "Meta Ads", color: "#1877F2" },
  { key: "google", label: "Google Ads", color: "#DB4437" },
  { key: "facebook", label: "Facebook Orgânico", color: "#4267B2" },
  { key: "tiktok", label: "TikTok Orgânico", color: "#111111" },
  { key: "padrao", label: "Padrão / Direto", color: "#C5A059" },
];

async function makeQrDataUrl(url, size = 1024) {
  return QRCode.toDataURL(url, {
    width: size,
    margin: 4,
    errorCorrectionLevel: "H",
    color: { dark: "#000000", light: "#FFFFFF" },
  });
}

function StatusBadge({ ok }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#EBF3EC] text-[#2D5A37]">
      <Check className="w-3 h-3" /> Cadastrado
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#FDF3E7] text-[#995C1D]">
      Pendente
    </span>
  );
}

function PrayerRow({ prayer, onChanged }) {
  const [qr, setQr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(prayer.title);
  const fileRef = useRef(null);
  const n = prayer.number;

  useEffect(() => {
    makeQrDataUrl(prayer.public_url, 256).then(setQr).catch(() => {});
  }, [prayer.public_url]);

  useEffect(() => setTitle(prayer.title), [prayer.title]);

  const copyLink = () => {
    navigator.clipboard.writeText(prayer.public_url);
    setCopied(true);
    toast.success(`Link da oração ${String(n).padStart(2, "0")} copiado`);
    setTimeout(() => setCopied(false), 1800);
  };

  const downloadQr = async () => {
    const hi = await makeQrDataUrl(prayer.public_url, 1024);
    const a = document.createElement("a");
    a.href = hi;
    a.download = `qr-oracao-${String(n).padStart(2, "0")}.png`;
    a.click();
    toast.success(`QR Code ${String(n).padStart(2, "0")} baixado`);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/prayers/${n}/audio`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Áudio da oração ${String(n).padStart(2, "0")} atualizado`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveTitle = async () => {
    try {
      await api.put(`/admin/prayers/${n}`, { title });
      toast.success("Título atualizado");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <tr data-testid={`prayer-row-${n}`} className="hover:bg-[#F4EFE6]/50 transition-colors border-b border-[#E5DCCF]/40 align-middle">
      <td className="px-4 py-4 font-cinzel text-[#C5A059] font-semibold whitespace-nowrap">{String(n).padStart(2, "0")}</td>
      <td className="px-4 py-4 min-w-[200px]">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              data-testid={`title-input-${n}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border border-[#E5DCCF] bg-[#FDFBF7] px-2 py-1 text-sm outline-none focus:border-[#C5A059]"
            />
            <button data-testid={`save-title-${n}`} onClick={saveTitle} className="text-[#2D5A37]"><Check className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <span className="font-serif-display text-[#3A2921] text-lg">{prayer.title}</span>
            <button data-testid={`edit-title-${n}`} onClick={() => setEditing(true)} className="opacity-40 hover:opacity-100 transition-opacity">
              <Pencil className="w-3.5 h-3.5 text-[#6E5A4F]" />
            </button>
          </div>
        )}
        {prayer.original_filename && (
          <span className="flex items-center gap-1 text-[11px] text-[#9C877A] mt-1">
            <FileAudio className="w-3 h-3" /> {prayer.original_filename}
          </span>
        )}
      </td>
      <td className="px-4 py-4">
        <a
          href={prayer.public_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[#6E5A4F] hover:text-[#C5A059] break-all max-w-[220px]"
        >
          <ExternalLink className="w-3 h-3 shrink-0" /> {prayer.public_url}
        </a>
      </td>
      <td className="px-4 py-4">
        {qr ? <img src={qr} alt={`QR ${n}`} className="w-14 h-14 rounded border border-[#E5DCCF]" /> : <div className="w-14 h-14 bg-[#EFE7DA] rounded animate-pulse" />}
      </td>
      <td className="px-4 py-4"><StatusBadge ok={prayer.has_audio} /></td>
      <td className="px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid={`copy-link-button-${n}`} onClick={copyLink} className="inline-flex items-center gap-1 rounded-lg border border-[#E5DCCF] px-2.5 py-1.5 text-xs text-[#3A2921] hover:border-[#C5A059] transition-colors">
            {copied ? <Check className="w-3.5 h-3.5 text-[#2D5A37]" /> : <Copy className="w-3.5 h-3.5" />} Copiar
          </button>
          <button data-testid={`download-qr-button-${n}`} onClick={downloadQr} className="inline-flex items-center gap-1 rounded-lg border border-[#E5DCCF] px-2.5 py-1.5 text-xs text-[#3A2921] hover:border-[#C5A059] transition-colors">
            <QrCode className="w-3.5 h-3.5" /> PNG
          </button>
          <input ref={fileRef} type="file" accept="audio/*,.mp3" className="hidden" onChange={upload} data-testid={`file-input-${n}`} />
          <button data-testid={`upload-mp3-button-${n}`} onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1 rounded-lg bg-[#3A2921] text-[#FDFBF7] px-2.5 py-1.5 text-xs hover:bg-[#5C4638] transition-colors disabled:opacity-50">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {prayer.has_audio ? "Substituir" : "Enviar MP3"}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminDashboard() {
  const [failedConv, setFailedConv] = useState(0);
  useEffect(() => {
    api.get("/admin/conversions/status").then((r) => {
      const c = r.data.counts || {};
      setFailedConv((c.error || 0) + (c.verification_failed || 0) + (c.expired || 0));
    }).catch(() => {});
  }, []);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [zipping, setZipping] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [generalQr, setGeneralQr] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [price, setPrice] = useState("");
  const [offerDeadline, setOfferDeadline] = useState("");
  const [savingLanding, setSavingLanding] = useState(false);
  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("all");

  const loadReport = useCallback((period) => {
    api.get(`/admin/visits/report?period=${period}`).then((res) => setReport(res.data)).catch(() => {});
  }, []);

  useEffect(() => { loadReport(reportPeriod); }, [reportPeriod, loadReport]);

  const generalUrl = `${(baseUrl || "").replace(/\/$/, "")}/oracoes`;

  useEffect(() => {
    if (baseUrl) makeQrDataUrl(generalUrl, 256).then(setGeneralQr).catch(() => {});
  }, [baseUrl, generalUrl]);

  const load = useCallback(() => {
    api.get("/prayers").then((res) => setPrayers(res.data)).catch(() => {});
    api.get("/config").then((res) => {
      setBaseUrl(res.data.public_base_url || "");
      setCheckoutUrl(res.data.checkout_url || "");
      setPrice(res.data.price || "");
      setOfferDeadline(res.data.offer_deadline || "");
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveLanding = async () => {
    setSavingLanding(true);
    try {
      await api.put("/admin/settings/landing", { checkout_url: checkoutUrl, price, offer_deadline: offerDeadline });
      toast.success("Landing page atualizada (link de compra e preço).");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingLanding(false);
    }
  };

  const saveBaseUrl = async () => {
    setSavingUrl(true);
    try {
      await api.put("/admin/settings/base-url", { public_base_url: baseUrl });
      toast.success("Domínio público atualizado. QR Codes agora apontam para o endereço final.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingUrl(false);
    }
  };

  const downloadGeneralQr = async () => {
    const hi = await makeQrDataUrl(generalUrl, 1024);
    const a = document.createElement("a");
    a.href = hi;
    a.download = "qr-oracoes-todas.png";
    a.click();
    toast.success("QR Code geral (/oracoes) baixado");
  };

  const downloadAllZip = async () => {
    setZipping(true);
    try {
      const zip = new JSZip();
      // QR geral da página com todas as orações
      const generalData = (await makeQrDataUrl(generalUrl, 1024)).split(",")[1];
      zip.file("qr-oracoes-todas.png", generalData, { base64: true });
      for (const p of prayers) {
        const dataUrl = await makeQrDataUrl(p.public_url, 1024);
        const base64 = dataUrl.split(",")[1];
        zip.file(`qr-oracao-${String(p.number).padStart(2, "0")}.png`, base64, { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, "qr-codes-quando-faltam-palavras.zip");
      toast.success("ZIP gerado: QR geral + 50 QR Codes");
    } catch (e) {
      toast.error("Erro ao gerar o ZIP");
    } finally {
      setZipping(false);
    }
  };

  const filtered = prayers.filter((p) => {
    const matchSearch =
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      String(p.number).padStart(2, "0").includes(search);
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "done" && p.has_audio) ||
      (statusFilter === "pending" && !p.has_audio);
    return matchSearch && matchStatus;
  });

  const doneCount = prayers.filter((p) => p.has_audio).length;
  const isPreview = baseUrl.includes("preview.emergentagent") || baseUrl.includes("localhost") || baseUrl === "";

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-[#E5DCCF] mb-8">
          <div>
            <h1 data-testid="admin-dashboard-title" className="font-cinzel uppercase tracking-[0.15em] text-lg text-[#3A2921]">
              Painel — 50 Orações
            </h1>
            <p className="text-sm text-[#9C877A] mt-1">
              {doneCount} de 50 orações com áudio cadastrado · {user?.email}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/conversoes"
              data-testid="admin-conversions-link"
              className="inline-flex items-center gap-2 rounded-xl border border-[#C5A059]/60 text-[#3A2921] px-4 py-2.5 text-sm font-semibold hover:bg-[#F4EFE6] transition-colors"
            >
              <BarChart3 className="w-4 h-4" /> Vendas & Conversões
              {failedConv > 0 && <span data-testid="admin-conversions-failure-badge" className="ml-1 rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5">{failedConv} falha(s)</span>}
            </Link>
            <button
              data-testid="download-all-qrs-zip-button"
              onClick={downloadAllZip}
              disabled={zipping}
              className="inline-flex items-center gap-2 rounded-xl bg-[#3A2921] text-[#FDFBF7] px-5 py-2.5 text-sm font-semibold hover:bg-[#5C4638] transition-colors border border-[#C5A059]/40 disabled:opacity-50"
            >
              {zipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Baixar todos os QR Codes (ZIP)
            </button>
            <button data-testid="logout-button" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-1 text-sm text-[#6E5A4F] hover:text-[#3A2921]">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>

        {/* Base URL setting */}
        <div className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5 mb-6">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#6E5A4F] font-semibold mb-2">
            <Link2 className="w-4 h-4" /> Domínio público final (usado nos QR Codes)
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="base-url-input"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://aprendafalarcomdeus.com.br"
              className="flex-1 rounded-xl border border-[#E5DCCF] bg-[#FDFBF7] px-4 py-2.5 text-sm outline-none focus:border-[#C5A059]"
            />
            <button
              data-testid="save-base-url-button"
              onClick={saveBaseUrl}
              disabled={savingUrl}
              className="rounded-xl bg-[#C5A059] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#B08B44] transition-colors disabled:opacity-50"
            >
              {savingUrl ? "Salvando..." : "Salvar domínio"}
            </button>
          </div>
          {isPreview && (
            <p className="text-xs text-[#995C1D] mt-2">
              ⚠️ Atenção: defina o domínio final aqui antes de gerar/imprimir os QR Codes definitivos. O endereço atual ainda é de preview/desenvolvimento.
            </p>
          )}
        </div>

        {/* QR Code Geral (/oracoes) */}
        <div className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5 mb-6 flex flex-col sm:flex-row items-center gap-5">
          <div className="shrink-0">
            {generalQr ? (
              <img data-testid="general-qr-preview" src={generalQr} alt="QR geral" className="w-24 h-24 rounded border border-[#E5DCCF]" />
            ) : (
              <div className="w-24 h-24 bg-[#EFE7DA] rounded animate-pulse" />
            )}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-cinzel uppercase tracking-widest text-xs text-[#3A2921] mb-1">QR Code Geral — Todas as Orações</h3>
            <p className="text-sm text-[#6E5A4F]">Aponta para a página com a lista das 50 orações.</p>
            <a href={generalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#C5A059] hover:text-[#B08B44] mt-1 break-all">
              <ExternalLink className="w-3 h-3" /> {generalUrl}
            </a>
          </div>
          <button
            data-testid="download-general-qr-button"
            onClick={downloadGeneralQr}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E5DCCF] px-4 py-2.5 text-sm text-[#3A2921] hover:border-[#C5A059] transition-colors shrink-0"
          >
            <QrCode className="w-4 h-4" /> Baixar QR geral (PNG)
          </button>
        </div>

        {/* Landing page settings */}
        <div className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5 mb-6">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#6E5A4F] font-semibold mb-3">
            <Link2 className="w-4 h-4" /> Landing page (link de compra e preço)
          </label>
          <div className="grid sm:grid-cols-3 gap-3">
            <input
              data-testid="checkout-url-input"
              value={checkoutUrl}
              onChange={(e) => setCheckoutUrl(e.target.value)}
              placeholder="Link de compra (Monetizze)"
              className="sm:col-span-2 rounded-xl border border-[#E5DCCF] bg-[#FDFBF7] px-4 py-2.5 text-sm outline-none focus:border-[#C5A059]"
            />
            <input
              data-testid="price-input"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="R$ 29,90"
              className="rounded-xl border border-[#E5DCCF] bg-[#FDFBF7] px-4 py-2.5 text-sm outline-none focus:border-[#C5A059]"
            />
          </div>
          <div className="mt-3">
            <label className="text-xs text-[#6E5A4F] font-semibold block mb-1">Prazo da oferta (opcional) — mostra contador na landing</label>
            <input
              data-testid="offer-deadline-input"
              type="datetime-local"
              value={offerDeadline}
              onChange={(e) => setOfferDeadline(e.target.value)}
              className="rounded-xl border border-[#E5DCCF] bg-[#FDFBF7] px-4 py-2.5 text-sm outline-none focus:border-[#C5A059]"
            />
          </div>
          <button
            data-testid="save-landing-button"
            onClick={saveLanding}
            disabled={savingLanding}
            className="mt-3 rounded-xl bg-[#C5A059] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#B08B44] transition-colors disabled:opacity-50"
          >
            {savingLanding ? "Salvando..." : "Salvar landing"}
          </button>
          {!checkoutUrl && (
            <p className="text-xs text-[#995C1D] mt-2">
              O botão "Quero meu exemplar" mostra um aviso até você colar o link de compra aqui.
            </p>
          )}
        </div>

        {/* Relatório de Origem (visitas) */}
        <div data-testid="origin-report" className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#6E5A4F] font-semibold">
              <BarChart3 className="w-4 h-4" /> Relatório de origem — visitas por canal
            </label>
            <div className="flex items-center gap-1 rounded-xl bg-[#F4EFE6] p-1">
              {[
                { k: "today", t: "Hoje" },
                { k: "7d", t: "7 dias" },
                { k: "30d", t: "30 dias" },
                { k: "all", t: "Tudo" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`report-period-${opt.k}`}
                  onClick={() => setReportPeriod(opt.k)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    reportPeriod === opt.k
                      ? "bg-[#3A2921] text-[#FDFBF7]"
                      : "text-[#6E5A4F] hover:bg-[#EFE7DA]"
                  }`}
                >
                  {opt.t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-baseline gap-2 mb-4">
            <span data-testid="report-total" className="font-cinzel text-3xl text-[#3A2921]">{report ? report.total : 0}</span>
            <span className="text-sm text-[#9C877A]">visitas no período</span>
          </div>

          <div className="space-y-3">
            {ORIGEM_META.map(({ key, label, color }) => {
              const count = report?.counts?.[key] || 0;
              const total = report?.total || 0;
              const pct = total ? Math.round((count / total) * 100) : 0;
              return (
                <div key={key} data-testid={`report-row-${key}`} className="flex items-center gap-3">
                  <span className="w-36 shrink-0 text-sm text-[#3A2921]">{label}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-[#EFE7DA] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                  <span className="w-24 shrink-0 text-right text-sm text-[#6E5A4F]">
                    <span data-testid={`report-count-${key}`} className="font-semibold text-[#3A2921]">{count}</span> · {pct}%
                  </span>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-[#9C877A] mt-4">
            💡 As <strong>compras</strong> por origem você acompanha direto na Monetizze (cada canal usa um link próprio). Aqui mostramos as <strong>visitas</strong> à página de vendas.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9C877A]" />
            <input
              data-testid="search-prayer-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número ou título..."
              className="w-full rounded-xl border border-[#E5DCCF] bg-[#FDFBF7] pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[#C5A059]"
            />
          </div>
          <select
            data-testid="status-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border border-[#E5DCCF] bg-[#FDFBF7] px-4 py-2.5 text-sm outline-none focus:border-[#C5A059]"
          >
            <option value="all">Todos os status</option>
            <option value="done">Cadastrados</option>
            <option value="pending">Pendentes</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] overflow-x-auto shadow-sm">
          <table data-testid="prayers-table" className="w-full text-left">
            <thead>
              <tr className="bg-[#F4EFE6] text-[#3A2921] font-cinzel uppercase text-xs tracking-wider border-b border-[#E5DCCF]">
                <th className="px-4 py-3">Oração</th>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">URL Pública</th>
                <th className="px-4 py-3">QR Code</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <PrayerRow key={p.number} prayer={p} onChanged={load} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
